import { useEffect, useRef, useState } from 'react'
import {
  PublishJob, disconnectPublisher, downloadUrl, getJob, openGooglePopup, publish,
} from '../lib/api'
import { useSession } from '../lib/session'
import { Clip, Source, formatTime, timelineLength } from '../lib/types'
import { Icon } from './Icon'

interface Props {
  sources: Source[]
  clips: Clip[]
  onBack(): void
  onStartOver(): void
}

export function PublishPanel({ sources, clips, onBack, onStartOver }: Props) {
  const { me, refresh } = useSession()
  const [title, setTitle] = useState(defaultTitle())
  const [description, setDescription] = useState('')
  const [job, setJob] = useState<PublishJob | null>(null)
  const [sending, setSending] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const pollRef = useRef<number>(0)

  const total = timelineLength(clips)
  const busy = !!job && job.stage !== 'done' && job.stage !== 'error'

  const publisher = me?.publisher
  const ready = Boolean(publisher?.connected)

  useEffect(() => () => window.clearInterval(pollRef.current), [])

  const connectChannel = () =>
    openGooglePopup('/api/publisher/connect', 'bloom:publisher-ok', () => { void refresh() })

  const start = async (renderOnly = false) => {
    setError(null)
    setSending(0)
    try {
      const { jobId } = await publish(
        sources, clips, { title, description, renderOnly }, (f) => setSending(f),
      )
      setJob({ id: jobId, stage: 'queued', progress: 0, message: 'Queued…' })
      pollRef.current = window.setInterval(async () => {
        try {
          const j = await getJob(jobId)
          setJob(j)
          if (j.stage === 'done' || j.stage === 'error') window.clearInterval(pollRef.current)
        } catch (e) {
          window.clearInterval(pollRef.current)
          setError((e as Error).message)
        }
      }, 1000)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const copy = async () => {
    if (!job?.url) return
    await navigator.clipboard.writeText(job.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  if (job?.stage === 'done' && !job.url) {
    return (
      <div className="card">
        <h2><Icon name="check" size={17} /> Your video is ready</h2>
        <p className="sub">
          It wasn't uploaded to YouTube, but the finished, fully-trimmed MP4 is waiting for you.
        </p>
        <div className="wrap-row">
          <a className="btn primary big" href={downloadUrl(job.id)} download>
            <Icon name="download" size={17} /> Download the MP4
          </a>
          <button className="btn" onClick={() => setJob(null)}>
            <Icon name="arrowLeft" /> Back
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onStartOver}>Record another</button>
        </div>
      </div>
    )
  }

  if (job?.stage === 'done') {
    return (
      <div className="card">
        <h2><Icon name="check" size={17} /> Published as unlisted</h2>
        <p className="sub">
          Only people with this link can watch it — it won't appear in search or on the channel page.
        </p>
        <div className="linkbox">
          <code>{job.url}</code>
          <button className="btn" onClick={copy}>
            <Icon name={copied ? 'check' : 'copy'} />
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <a className="btn" href={job.url} target="_blank" rel="noreferrer">
            <Icon name="external" /> Open
          </a>
        </div>
        <div style={{ height: 14 }} />
        <p className="small muted">
          YouTube still needs a few minutes to finish processing before higher resolutions appear.
        </p>
        <div style={{ height: 16 }} />
        <div className="wrap-row">
          <a className="btn" href={downloadUrl(job.id)} download>
            <Icon name="download" /> Download the MP4
          </a>
          <button className="btn primary" onClick={onStartOver}>Record another</button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card">
        <h2>Publish to YouTube</h2>
        <p className="sub">
          {formatTime(total)} of finished video from {clips.length} piece{clips.length === 1 ? '' : 's'}.
          It'll be uploaded as <strong>unlisted</strong>: link-only, never public. Your name is
          added to the description so colleagues can tell whose recording it is.
        </p>

        {publisher?.error && (
          <><div className="notice error">{publisher.error}</div><div style={{ height: 12 }} /></>
        )}

        {!ready && (
          <div className="notice warn">
            <strong>The shared channel isn’t connected.</strong>{' '}
            {me?.canManagePublisher
              ? 'Connect the Google account that owns the AIK Innovations YouTube channel — once, for everyone.'
              : 'Ask a Bloom administrator to connect it. You can still download the finished MP4 below.'}
            {me?.canManagePublisher && (
              <>
                <div style={{ height: 10 }} />
                <button className="btn primary small" onClick={connectChannel}>
                  Connect the shared channel
                </button>
              </>
            )}
          </div>
        )}

        {ready && (
          <div className="wrap-row">
            <span className="notice ok" style={{ flex: 1 }}>
              Publishing to <strong>{publisher?.channel?.title ?? 'the shared channel'}</strong>
              {publisher?.connectedBy && (
                <span className="small muted"> · connected by {publisher.connectedBy}</span>
              )}
            </span>
            {me?.canManagePublisher && (
              <button
                className="btn ghost small"
                onClick={async () => { await disconnectPublisher(); await refresh() }}
              >
                Disconnect
              </button>
            )}
          </div>
        )}

        <div style={{ height: 16 }} />

        <div className="field">
          <label htmlFor="title">Title</label>
          <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div style={{ height: 12 }} />
        <div className="field">
          <label htmlFor="desc">Description</label>
          <textarea
            id="desc"
            value={description}
            placeholder="Optional — what's in this recording?"
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {error && (<><div style={{ height: 14 }} /><div className="notice error">{error}</div></>)}

        {(busy || sending > 0) && (
          <>
            <div style={{ height: 18 }} />
            <div className="progress">
              <i style={{ width: `${Math.round(progressOf(job, sending) * 100)}%` }} />
            </div>
            <div style={{ height: 8 }} />
            <p className="small muted" style={{ margin: 0 }}>{stageText(job, sending)}</p>
          </>
        )}

        {job?.stage === 'error' && (
          <><div style={{ height: 14 }} /><div className="notice error">{job.error}</div></>
        )}

        <div style={{ height: 20 }} />
        <div className="wrap-row">
          <button className="btn" onClick={onBack} disabled={busy}>
            <Icon name="arrowLeft" /> Back to editing
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={() => start(true)} disabled={busy}>
            <Icon name="download" /> Just download the MP4
          </button>
          <button
            className="btn primary big"
            onClick={() => start(false)}
            disabled={busy || !title.trim() || !ready}
          >
            {busy ? 'Working…' : 'Upload as unlisted'}
          </button>
        </div>
        {!ready && (
          <p className="small muted" style={{ marginTop: 10 }}>
            Uploading needs the shared channel connected — or just download the file instead.
          </p>
        )}
      </div>
    </>
  )
}

function progressOf(job: PublishJob | null, sending: number) {
  if (!job) return sending * 0.25 // sending takes to our own server
  return 0.25 + job.progress * 0.75
}

function stageText(job: PublishJob | null, sending: number) {
  if (!job) return `Sending your takes to the Bloom server… ${Math.round(sending * 100)}%`
  if (job.stage === 'queued') return 'Queued…'
  return job.message || job.stage
}

function defaultTitle() {
  const d = new Date()
  return `Bloom recording — ${d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  })}`
}
